import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "@outpave/ds";
import { toast } from "react-toastify";
import { Button, Typography } from "@mui/material";
import { Stack } from "../layout/reusableLayouts";

import CardPolicyForm from "components/common/cards/CardPolicyForm";
import { useCardPresetData } from "hooks/useCardPresetData";
import { getCardPolicyInitialValues } from "utils/cards/cardFormUtils";
import { CardPreset, cardPresetFromPolicy } from "utils/cards/cardPresets";
import CardPresetTextBox from "./cards/CardPresetTextBox";

interface CardPresetConfigDrawerProps {
  selectedId?: string;
  open: boolean;
  handleCloseDrawer: (isRefetchData?: boolean) => void;
  onCreatePresetSuccess?: (preset: CardPreset) => void;
}

const CardPresetConfigDrawer: React.FC<CardPresetConfigDrawerProps> = (
  props,
) => {
  const { selectedId, open, handleCloseDrawer, onCreatePresetSuccess } = props;

  const { t } = useTranslation();

  const {
    formikCardPreset,
    specificLoadingStates,
    atLeastOneLimit,
    handleSubmitCardPreset,
  } = useCardPresetData({
    presetId: selectedId,
  });

  const handleSubmit = async () => {
    try {
      const result = await handleSubmitCardPreset();
      if (selectedId) {
        toast.success(t("Preset updated"));
      } else {
        toast.success(t("Preset added!"));
      }
      if (onCreatePresetSuccess) {
        onCreatePresetSuccess(
          cardPresetFromPolicy(
            formikCardPreset.values,
            result.data.addCardPreset.id,
            formikCardPreset.values.nameOfPreset ?? "",
          ),
        );
      }
      formikCardPreset.resetForm({ values: getCardPolicyInitialValues() });
      handleCloseDrawer(true);
    } catch (error: unknown) {
      const graphQLErrors = (error as { graphQLErrors?: { message: string }[] })
        ?.graphQLErrors;
      toast.error(graphQLErrors?.[0]?.message ?? t("Please try again!"));
    }
  };

  useEffect(() => {
    if (open && !selectedId) {
      // ie: if we are adding a new card preset
      formikCardPreset.resetForm({ values: getCardPolicyInitialValues() });
    }
    if (open) {
      formikCardPreset.setTouched({}, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const busy =
    specificLoadingStates.addingCardPreset ||
    specificLoadingStates.updatingCardPreset;

  // The legacy backdrop skipped the setTouched reset the header X and Back
  // button did; the DS has one close path.
  const handleClose = () => {
    if (busy) return;
    formikCardPreset.setTouched({}, false);
    handleCloseDrawer();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={t("Card Settings")}
      loading={specificLoadingStates.loadingCardPresetData || busy}
      footer={
        <>
          <Button
            variant="primary-outlined"
            onClick={handleClose}
            disabled={busy}
          >
            {t("Back")}
          </Button>

          <Button
            variant="primary-filled"
            disabled={
              !formikCardPreset.isValid ||
              !formikCardPreset.dirty ||
              specificLoadingStates.loadingCardPresetData ||
              busy ||
              !atLeastOneLimit
            }
            onClick={handleSubmit}
          >
            {t("Save")}
          </Button>
        </>
      }
    >
      {!specificLoadingStates.loadingCardPresetData && (
        <>
          <Stack gap="var(--space-6)">
            <Stack gap="var(--space-2)">
              <Typography variant="h5">
                {!!selectedId ? t("Edit Preset") : t("Create Preset")}
              </Typography>
              <Typography variant="body1">
                {t("PresetCardLimitDescription")}
              </Typography>
            </Stack>

            <CardPresetTextBox formik={formikCardPreset} />
          </Stack>
          <CardPolicyForm formik={formikCardPreset} variant="preset" />
        </>
      )}
    </Drawer>
  );
};

export default CardPresetConfigDrawer;
